import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  getDocs,
  limit
} from 'firebase/firestore';
import { db } from '../utils/firebase';
import { useAuth } from './useAuth';
import { getWeekString } from '../utils/dateUtils';

export function useWeeklyReview() {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  // Subscribe to user's weekly reviews
  useEffect(() => {
    if (!user?.uid) {
      setReviews([]);
      setLoading(false);
      return;
    }

    const reviewsRef = collection(db, 'weeklyReviews');
    const q = query(
      reviewsRef,
      where('userId', '==', user.uid),
      orderBy('completedAt', 'desc'),
      limit(52) // Last year of reviews
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const reviewData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setReviews(reviewData);
      setLoading(false);
    }, (error) => {
      console.error('Error fetching weekly reviews:', error);
      setLoading(false);
    });

    return unsubscribe;
  }, [user?.uid]);

  // Save a new weekly review
  const saveWeeklyReview = useCallback(async (reviewData) => {
    if (!user?.uid) return null;

    try {
      const docRef = await addDoc(collection(db, 'weeklyReviews'), {
        ...reviewData,
        userId: user.uid,
        completedAt: new Date(),
        startedAt: reviewData.startedAt || new Date()
      });
      return docRef.id;
    } catch (error) {
      console.error('Error saving weekly review:', error);
      throw error;
    }
  }, [user?.uid]);

  // Get review for a specific week
  const getReviewForWeek = useCallback(async (weekString) => {
    if (!user?.uid) return null;

    try {
      const reviewsRef = collection(db, 'weeklyReviews');
      const q = query(
        reviewsRef,
        where('userId', '==', user.uid),
        where('week', '==', weekString),
        limit(1)
      );
      const snapshot = await getDocs(q);

      if (snapshot.empty) return null;

      return {
        id: snapshot.docs[0].id,
        ...snapshot.docs[0].data()
      };
    } catch (error) {
      console.error('Error fetching review for week:', error);
      return null;
    }
  }, [user?.uid]);

  // Check if current week has been reviewed
  const hasReviewedCurrentWeek = useCallback(() => {
    const currentWeek = getWeekString(new Date());
    return reviews.some(r => r.week === currentWeek);
  }, [reviews]);

  // Get latest review
  const getLatestReview = useCallback(() => {
    return reviews.length > 0 ? reviews[0] : null;
  }, [reviews]);

  // Get metrics trends (for charts)
  const getMetricsTrends = useCallback((metricKey, numWeeks = 12) => {
    const recentReviews = reviews.slice(0, numWeeks).reverse();
    return recentReviews.map(r => ({
      week: r.week,
      value: r.metrics?.[metricKey] ?? 0,
      completedAt: r.completedAt
    }));
  }, [reviews]);

  // Get average metrics
  const getAverageMetrics = useCallback((numWeeks = 4) => {
    const recentReviews = reviews.slice(0, numWeeks);
    if (recentReviews.length === 0) return null;

    const metrics = {
      tasksCompletedCount: 0,
      habitsCompletionRate: 0,
      projectsReviewedCount: 0
    };

    recentReviews.forEach(r => {
      if (r.metrics) {
        metrics.tasksCompletedCount += r.metrics.tasksCompletedCount || 0;
        metrics.habitsCompletionRate += r.metrics.habitsCompletionRate || 0;
        metrics.projectsReviewedCount += r.metrics.projectsReviewedCount || 0;
      }
    });

    const count = recentReviews.length;
    return {
      tasksCompletedCount: Math.round(metrics.tasksCompletedCount / count),
      habitsCompletionRate: Math.round(metrics.habitsCompletionRate / count),
      projectsReviewedCount: Math.round(metrics.projectsReviewedCount / count)
    };
  }, [reviews]);

  return {
    reviews,
    loading,
    saveWeeklyReview,
    getReviewForWeek,
    hasReviewedCurrentWeek,
    getLatestReview,
    getMetricsTrends,
    getAverageMetrics
  };
}
